// src/components/cards/VenueListItemCard.jsx
// Horizontal list card, image left · content right · all CTAs.
// Rebuilt April 2026: image cycling, video w/ sound toggle, cinematic gradient,
// inclusions strip, proper location fallback, premium spacing.

import { useState, useEffect, useRef } from "react";
import { imgUrl } from "../../utils/imgUrl";
import { trackCompareAdd } from "../../services/userEventService";
import { useTheme } from "../../theme/ThemeContext";
import { useShortlist } from "../../shortlist/ShortlistContext";
import Stars from "../ui/Stars";
import ShortlistButton from "../buttons/ShortlistButton";
import CompareCheckbox from "../buttons/CompareCheckbox";
import LuxeEnquiryModal from "../enquiry/LuxeEnquiryModal";
import TierBadge from "../editorial/TierBadge";
import { getQualityTier } from "../../services/listings";

const GOLD   = "#d39a21";
const GD     = "var(--font-heading-primary)";
const NU     = "var(--font-body)";
const CYCLE_MS = 2200; // ms per image when hovering

const COMPARE_KEY = "lwd_compare_list";
function loadCompareList() {
  try { return JSON.parse(sessionStorage.getItem(COMPARE_KEY)) || []; }
  catch { return []; }
}
function saveCompareList(list) {
  try { sessionStorage.setItem(COMPARE_KEY, JSON.stringify(list)); }
  catch {}
}

// Parse YouTube / Vimeo URL into { type, id }
function parseVideoUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (yt) return { type: "youtube", id: yt[1] };
  const vm = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
  if (vm) return { type: "vimeo", id: vm[1] };
  return null;
}

// Resolve location string with full fallback chain
function resolveLocation(v) {
  const parts = [];
  const city   = v.city   || v.cityName   || null;
  const region = v.region || v.regionName || (v.regionSlug ? v.regionSlug.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join(" ") : null);
  const country = v.country || v.countryName || null;
  if (city)   parts.push(city);
  if (region && region !== city) parts.push(region);
  if (country && parts.length === 0) parts.push(country);
  return parts.join(", ");
}

export default function VenueListItemCard({ v, onView, isHighlighted, quickViewItem, setQuickViewItem }) {
  const C   = useTheme();
  const { isShortlisted, toggleItem } = useShortlist();

  const [hov,          setHov]          = useState(false);
  const [imgIdx,       setImgIdx]       = useState(0);
  const [imgFading,    setImgFading]    = useState(false);
  const [muted,        setMuted]        = useState(true);
  const [isVisible,    setIsVisible]    = useState(false);
  const [showEnquiry,  setShowEnquiry]  = useState(false);
  const [compareList,  setCompareList]  = useState(loadCompareList);
  const cycleRef   = useRef(null);
  const hovRef     = useRef(false);
  const cardRef    = useRef(null);
  const videoRef   = useRef(null);
  const iframeRef  = useRef(null);

  // ── Build media array: images first, video last ──────────────────────────
  const allMedia = (() => {
    const items = [];
    (v.imgs || []).forEach(i => {
      const src = typeof i === "string" ? i : (i?.src || i?.url || "");
      if (src) items.push({ type: "image", src });
    });
    if (v.videoUrl) {
      const parsed = parseVideoUrl(v.videoUrl);
      if (parsed) {
        items.push({ type: parsed.type, embedId: parsed.id, src: v.videoUrl });
      } else {
        items.push({ type: "video", src: v.videoUrl });
      }
    }
    return items;
  })();

  const mediaCount  = allMedia.length;
  const hasMultiple = mediaCount > 1;
  const curMedia    = allMedia[imgIdx] || null;
  const isVideoSlide = curMedia && (curMedia.type === "video" || curMedia.type === "youtube" || curMedia.type === "vimeo");
  const hasVideo    = allMedia.some(m => m.type === "video" || m.type === "youtube" || m.type === "vimeo");

  // ── IntersectionObserver, pause/reset when off-screen ───────────────────
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.3;
        setIsVisible(visible);
        if (!visible) setMuted(true);
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // ── Native video: play/pause + mute based on visibility ──────────────────
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = muted;
    if (isVideoSlide && isVisible) {
      vid.currentTime = 0;
      vid.play().catch(() => {});
    } else {
      vid.pause();
    }
  }, [imgIdx, isVisible, muted, isVideoSlide]);

  // ── Iframe (YouTube / Vimeo): mute + play/pause ───────────────────────────
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !curMedia) return;
    if (curMedia.type === "youtube") {
      const cmd = muted ? "mute" : "unMute";
      iframe.contentWindow?.postMessage(JSON.stringify({ event: "command", func: cmd, args: [] }), "*");
      if (isVisible) {
        iframe.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
      } else {
        iframe.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), "*");
      }
    } else if (curMedia.type === "vimeo") {
      iframe.contentWindow?.postMessage(JSON.stringify({ method: "setVolume", value: muted ? 0 : 1 }), "*");
      if (isVisible) {
        iframe.contentWindow?.postMessage(JSON.stringify({ method: "play" }), "*");
      } else {
        iframe.contentWindow?.postMessage(JSON.stringify({ method: "pause" }), "*");
      }
    }
  }, [imgIdx, isVisible, muted, curMedia]);

  // Manual left/right navigation
  function goImg(dir, e) {
    e.stopPropagation();
    clearInterval(cycleRef.current);
    setImgFading(true);
    setTimeout(() => {
      setImgIdx(i => (i + dir + mediaCount) % mediaCount);
      setImgFading(false);
      if (hovRef.current && hasMultiple) {
        cycleRef.current = setInterval(() => {
          setImgFading(true);
          setTimeout(() => {
            setImgIdx(i2 => (i2 + 1) % mediaCount);
            setImgFading(false);
          }, 280);
        }, CYCLE_MS);
      }
    }, 280);
  }

  // Auto-cycle on hover (images only, stops when on video slide)
  useEffect(() => {
    if (hov && hasMultiple && !isVideoSlide) {
      cycleRef.current = setInterval(() => {
        setImgFading(true);
        setTimeout(() => {
          setImgIdx(i => (i + 1) % mediaCount);
          setImgFading(false);
        }, 280);
      }, CYCLE_MS);
    } else {
      clearInterval(cycleRef.current);
      if (!hov) { setImgIdx(0); setImgFading(false); }
    }
    return () => clearInterval(cycleRef.current);
  }, [hov, hasMultiple, isVideoSlide, mediaCount]);

  // Keep compareList in sync across cards
  useEffect(() => {
    const sync = () => setCompareList(loadCompareList());
    window.addEventListener("lwd:compare-bar", sync);
    return () => window.removeEventListener("lwd:compare-bar", sync);
  }, []);

  const active     = hov || isHighlighted;
  const isCompared = compareList.some(i => i.id === v.id);
  const price      = v.priceFrom || v.price || null;
  const guests     = v.capacity  || v.maxGuests || null;
  const style      = v.styles?.[0] || v.style || null;
  const location   = resolveLocation(v);
  // v.amenities is a free-text description on most listings (a data-shape mismatch,
  // not an array of amenity tags — confirmed on 548/552 production listings, not
  // Spain-specific). .slice(0,4) on a non-empty string silently returns a 4-char
  // string that passes the `.length > 0` check below but has no .map(), crashing
  // the whole page. Guard for a real array before treating either field as a list.
  const rawInclusions = Array.isArray(v.inclusions) ? v.inclusions
    : Array.isArray(v.amenities) ? v.amenities
    : [];
  const inclusions = rawInclusions.slice(0, 4);

  return (
    <article
      ref={cardRef}
      onMouseEnter={() => { setHov(true);  hovRef.current = true;  }}
      onMouseLeave={() => { setHov(false); hovRef.current = false; }}
      onClick={() => onView?.(v)}
      style={{
        display:       "flex",
        flexDirection: "row",
        alignItems:    "stretch",
        background:    active ? "rgba(211,154,33,0.04)" : C.card,
        border:        `1px solid ${active ? "rgba(211,154,33,0.28)" : C.border}`,
        borderRadius:  "var(--lwd-radius-card)",
        overflow:      "hidden",
        cursor:        "pointer",
        transition:    "border-color 0.3s ease, box-shadow 0.3s ease, background 0.3s ease",
        boxShadow:     active ? "0 8px 32px rgba(0,0,0,0.18)" : "none",
        flexShrink:    0,
      }}
    >

      {/* ── IMAGE ─────────────────────────────────────────────────────────── */}
      <div style={{
        width:      357,
        minWidth:   357,
        height:     357,
        position:   "relative",
        overflow:   "hidden",
        background: "#0a0806",
        flexShrink: 0,
        alignSelf:  "flex-start",
      }}>

        {/* ── Media: image / native video / YouTube / Vimeo ── */}
        {curMedia?.type === "image" && curMedia.src && (
          <img
            src={imgUrl(curMedia.src, { width: 700, quality: 72 })}
            alt={v.name}
            loading="lazy"
            decoding="async"
            style={{
              width: "100%", height: "100%", objectFit: "cover", display: "block",
              opacity:    imgFading ? 0 : 1,
              transform:  active && !imgFading ? "scale(1.04)" : "scale(1)",
              transition: imgFading ? "opacity 0.28s ease" : "transform 0.7s cubic-bezier(0.16,1,0.3,1), opacity 0.28s ease",
            }}
          />
        )}

        {curMedia?.type === "video" && (
          <>
            {allMedia[0]?.type === "image" && (
              <img src={imgUrl(allMedia[0].src, { width: 800 })} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            )}
            <video
              ref={videoRef}
              src={curMedia.src}
              muted={muted}
              loop playsInline preload="metadata"
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover",
                opacity: imgFading ? 0 : 1, transition: "opacity 0.28s ease",
              }}
            />
          </>
        )}

        {(curMedia?.type === "youtube" || curMedia?.type === "vimeo") && (
          <iframe
            ref={iframeRef}
            src={curMedia.type === "youtube"
              ? `https://www.youtube.com/embed/${curMedia.embedId}?autoplay=1&mute=1&loop=1&playlist=${curMedia.embedId}&controls=0&showinfo=0&rel=0&enablejsapi=1&modestbranding=1&playsinline=1`
              : `https://player.vimeo.com/video/${curMedia.embedId}?background=1&autoplay=1&loop=1&muted=1`
            }
            allow="autoplay; encrypted-media"
            title={`${v.name} video`}
            style={{
              position: "absolute", inset: 0, border: "none", pointerEvents: "none",
              width: "177.78%", height: "100%", left: "50%", transform: "translateX(-50%)",
              opacity: imgFading ? 0 : 1, transition: "opacity 0.28s ease",
            }}
          />
        )}

        {/* Cinematic gradient, bottom fade */}
        <div style={{
          position:   "absolute",
          inset:      0,
          background: "linear-gradient(to top, rgba(10,8,6,0.72) 0%, rgba(10,8,6,0.18) 40%, transparent 70%)",
          pointerEvents: "none",
        }} />

        {/* Top-left badges */}
        <div style={{ position: "absolute", top: 10, left: 10, display: "flex", flexDirection: "column", gap: 4 }}>
          {(v.featured || v.tag) && (
            <span style={{
              fontFamily: NU, fontSize: 8, fontWeight: 700, letterSpacing: "0.9px",
              textTransform: "uppercase", color: "#0f0d0a",
              background: `linear-gradient(135deg, ${GOLD}, #e8c97a)`,
              borderRadius: 16, padding: "3px 9px",
            }}>
              {v.featured ? "Editor's Pick" : v.tag}
            </span>
          )}
          {v.showcaseUrl && (
            <a
              href={v.showcaseUrl}
              aria-label={`View ${v.name} showcase`}
              onClick={e => e.stopPropagation()}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "2px 7px", borderRadius: 16,
                background: "rgba(211,154,33,0.15)", border: "1px solid rgba(211,154,33,0.3)",
                textDecoration: "none",
              }}
            >
              <span aria-hidden="true" style={{ color: GOLD, fontSize: 7 }}>✦</span>
              <span aria-hidden="true" style={{ fontFamily: NU, fontSize: 7, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: GOLD }}>
                Showcase
              </span>
            </a>
          )}
        </div>

        {/* Verified, bottom left */}
        {v.verified && (
          <div style={{ position: "absolute", bottom: 10, left: 10 }}>
            <span style={{
              fontFamily: NU, fontSize: 8, fontWeight: 600, letterSpacing: "0.3px",
              color: "#22c55e", background: "rgba(0,0,0,0.65)", borderRadius: 16,
              padding: "2px 7px", display: "flex", alignItems: "center", gap: 3,
            }}>✓ Verified</span>
          </div>
        )}

        {/* Media dots, bottom right */}
        {hasMultiple && (
          <div style={{ position: "absolute", bottom: 10, right: 10, display: "flex", gap: 4 }}>
            {allMedia.slice(0, 6).map((m, i) => (
              <span key={i} style={{
                width: 4, height: 4, borderRadius: "50%",
                background: i === imgIdx
                  ? (m.type !== "image" ? "#e8c97a" : GOLD)
                  : "rgba(255,255,255,0.4)",
                transition: "background 0.3s ease",
              }} />
            ))}
          </div>
        )}

        {/* Mute / unmute toggle, video slides only */}
        {isVideoSlide && (
          <button
            onClick={e => { e.stopPropagation(); setMuted(m => !m); }}
            aria-label={muted ? "Unmute" : "Mute"}
            style={{
              position: "absolute", bottom: 44, right: 10, zIndex: 5,
              width: 30, height: 30, borderRadius: "50%",
              background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.2)",
              color: muted ? "rgba(255,255,255,0.55)" : "#fff",
              cursor: "pointer", padding: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "color 0.2s, background 0.2s",
            }}
            onMouseEnter={e => (e.currentTarget.style.background = "rgba(0,0,0,0.75)")}
            onMouseLeave={e => (e.currentTarget.style.background = "rgba(0,0,0,0.5)")}
          >
            {muted ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
              </svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
            )}
          </button>
        )}

        {/* Shortlist heart */}
        <div style={{ position: "absolute", top: 10, right: 10, zIndex: 4 }} onClick={e => e.stopPropagation()}>
          <ShortlistButton
            item={{ id: v.id, name: v.name, image: v.imgs?.[0], category: "venue", price: v.priceFrom, type: "venue" }}
            isShortlisted={isShortlisted(v.id)}
            onToggle={() => toggleItem({ id: v.id, name: v.name, image: v.imgs?.[0], category: "venue", price: v.priceFrom, type: "venue" })}
            variant="icon"
            size="medium"
            strokeColor="#ffffff"
          />
        </div>

        {/* Left / Right nav arrows */}
        {hasMultiple && (
          <>
            <button
              onClick={e => goImg(-1, e)}
              style={{
                position: "absolute", top: "50%", left: 10,
                transform: "translateY(-50%)",
                width: 30, height: 30, borderRadius: "50%",
                background: "rgba(0,0,0,0.48)",
                backdropFilter: "blur(6px)",
                border: "1px solid rgba(255,255,255,0.16)",
                cursor: "pointer", padding: 0, zIndex: 3,
                display: "flex", alignItems: "center", justifyContent: "center",
                opacity: hov ? 1 : 0,
                transition: "opacity 0.35s ease, background 0.2s ease",
                pointerEvents: hov ? "auto" : "none",
              }}
              onMouseEnter={e => (e.currentTarget.style.background = "rgba(0,0,0,0.72)")}
              onMouseLeave={e => (e.currentTarget.style.background = "rgba(0,0,0,0.48)")}
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                <path d="M7 1.5L3 5.5L7 9.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              onClick={e => goImg(1, e)}
              style={{
                position: "absolute", top: "50%", right: 10,
                transform: "translateY(-50%)",
                width: 30, height: 30, borderRadius: "50%",
                background: "rgba(0,0,0,0.48)",
                backdropFilter: "blur(6px)",
                border: "1px solid rgba(255,255,255,0.16)",
                cursor: "pointer", padding: 0, zIndex: 3,
                display: "flex", alignItems: "center", justifyContent: "center",
                opacity: hov ? 1 : 0,
                transition: "opacity 0.35s ease, background 0.2s ease",
                pointerEvents: hov ? "auto" : "none",
              }}
              onMouseEnter={e => (e.currentTarget.style.background = "rgba(0,0,0,0.72)")}
              onMouseLeave={e => (e.currentTarget.style.background = "rgba(0,0,0,0.48)")}
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                <path d="M4 1.5L8 5.5L4 9.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </>
        )}
      </div>

      {/* ── CONTENT ───────────────────────────────────────────────────────── */}
      <div style={{
        flex:           1,
        padding:        "22px 24px 20px",
        display:        "flex",
        flexDirection:  "column",
        justifyContent: "space-between",
        minWidth:       0,
        position:       "relative",
      }}>

        {/* Compare, top right */}
        <div style={{ position: "absolute", top: 18, right: 20, zIndex: 4 }} onClick={e => e.stopPropagation()}>
          <CompareCheckbox
            item={{ id: v.id, name: v.name, image: v.imgs?.[0], category: "venue", price: v.priceFrom, type: "venue" }}
            isCompared={isCompared}
            onToggle={() => {
              const current  = loadCompareList();
              const alreadyIn = current.some(i => i.id === v.id);
              let updated;
              if (alreadyIn) {
                updated = current.filter(i => i.id !== v.id);
              } else {
                if (current.length >= 3) return;
                updated = [...current, { id: v.id, name: v.name }];
                trackCompareAdd({ venueId: v.id, venueName: v.name, compareList: current, sourceSurface: "list_card" });
              }
              saveCompareList(updated);
              setCompareList(updated);
              window.dispatchEvent(new CustomEvent("lwd:compare-bar", { detail: { active: updated.length > 0 } }));
            }}
          />
        </div>

        {/* ── Top block ── */}
        <div>

          {/* Name */}
          <h3 style={{
            fontFamily:    GD,
            fontSize:      24,
            fontStyle:     "italic",
            fontWeight:    500,
            color:         C.white,
            lineHeight:    1.15,
            margin:        "0 0 4px",
            paddingRight:  36,
          }}>
            {v.name}
          </h3>

          {/* Location */}
          {location && (
            <div style={{
              fontFamily:    NU,
              fontSize:      12,
              color:         C.grey,
              marginBottom:  10,
              letterSpacing: "0.2px",
            }}>
              {location}
            </div>
          )}

          {/* Meta row: style pill · rating · tier */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            {style && (
              <span style={{
                fontFamily: NU, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.9px",
                textTransform: "uppercase", color: GOLD,
                background: "rgba(211,154,33,0.1)", border: "1px solid rgba(211,154,33,0.22)",
                borderRadius: 16, padding: "2px 9px", flexShrink: 0,
              }}>
                {style}
              </span>
            )}
            {v.rating > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Stars rating={v.rating} size={11} color={GOLD} />
                <span style={{ fontFamily: NU, fontSize: 11, color: C.grey }}>
                  {v.rating.toFixed(1)} ({v.reviews || v.reviewCount || 0})
                </span>
              </div>
            )}
            {v.contentScore !== undefined && (
              <TierBadge tier={getQualityTier(v.contentScore)} showLabel size="sm" />
            )}
          </div>

          {/* Capacity */}
          {guests && (
            <div style={{
              fontFamily:   NU,
              fontSize:     11,
              color:        C.grey,
              marginBottom: 8,
              letterSpacing: "0.1px",
            }}>
              ✦ Up to {guests} guests
            </div>
          )}

          {/* Description */}
          {v.desc && (
            <p style={{
              fontFamily:           NU,
              fontSize:             14,
              color:                C.grey,
              lineHeight:           1.6,
              margin:               "0 0 12px",
              display:              "-webkit-box",
              WebkitLineClamp:      3,
              WebkitBoxOrient:      "vertical",
              overflow:             "hidden",
            }}>
              {v.desc}
            </p>
          )}

          {/* Inclusions strip */}
          {inclusions.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
              {inclusions.map((inc, i) => (
                <span key={i} style={{
                  fontFamily:    NU,
                  fontSize:      10,
                  color:         C.grey,
                  background:    "rgba(255,255,255,0.04)",
                  border:        `1px solid ${C.border}`,
                  borderRadius:  12,
                  padding:       "2px 8px",
                  letterSpacing: "0.2px",
                }}>
                  {inc}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── Bottom: price + CTAs ── */}
        <div style={{
          display:      "flex",
          alignItems:   "center",
          flexWrap:     "wrap",
          gap:          "6px 10px",
          marginTop:    "auto",
          paddingTop:   12,
          borderTop:    `1px solid ${C.border}`,
        }}>
          {price && (
            <div style={{ fontFamily: GD, fontSize: 18, color: C.gold, fontStyle: "italic", letterSpacing: "-0.4px", marginRight: "auto" }}>
              <span style={{ fontFamily: NU, fontSize: 10, fontWeight: 400, color: C.grey, marginRight: 3 }}>From</span>
              {price}
            </div>
          )}

          <button
            onClick={e => { e.stopPropagation(); setQuickViewItem?.(v); }}
            style={{
              fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: "1px",
              textTransform: "uppercase", color: GOLD,
              background: "rgba(211,154,33,0.08)", border: "1px solid rgba(211,154,33,0.25)",
              borderRadius: 4, padding: "7px 12px", cursor: "pointer", whiteSpace: "nowrap",
              transition: "background 0.2s, border-color 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(211,154,33,0.14)"; e.currentTarget.style.borderColor = "rgba(211,154,33,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(211,154,33,0.08)"; e.currentTarget.style.borderColor = "rgba(211,154,33,0.25)"; }}
          >
            Quick View
          </button>

          <button
            onClick={e => { e.stopPropagation(); setShowEnquiry(true); }}
            style={{
              fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: "1px",
              textTransform: "uppercase", color: "#0f0d0a",
              background: `linear-gradient(135deg, ${GOLD}, #e8c97a)`,
              border: "1px solid transparent", borderRadius: 4,
              padding: "7px 14px", cursor: "pointer", whiteSpace: "nowrap",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.88")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
          >
            Enquire
          </button>

          <button
            onClick={e => { e.stopPropagation(); onView?.(v); }}
            style={{
              fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: "1px",
              textTransform: "uppercase", color: GOLD,
              background: "rgba(211,154,33,0.08)", border: "1px solid rgba(211,154,33,0.25)",
              borderRadius: 4, padding: "7px 12px", cursor: "pointer", whiteSpace: "nowrap",
              transition: "background 0.2s, border-color 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(211,154,33,0.14)"; e.currentTarget.style.borderColor = "rgba(211,154,33,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(211,154,33,0.08)"; e.currentTarget.style.borderColor = "rgba(211,154,33,0.25)"; }}
          >
            Profile ›
          </button>
        </div>
      </div>

      {showEnquiry && (
        <LuxeEnquiryModal venue={v} onClose={() => setShowEnquiry(false)} entityType="venue" />
      )}
    </article>
  );
}
